import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Shield, HeartHandshake, CalendarClock, Wallet, FileText, Bell, LogOut, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { NAV } from "@/constants/testIds";

export default function TopNav() {
    const { user, logout } = useAuth();
    const nav = useNavigate();

    return (
        <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-stone-200">
            <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
                <Link to="/" data-testid={NAV.logo} className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-xl bg-emerald-700 flex items-center justify-center shrink-0">
                        <HeartHandshake className="w-5 h-5 text-white" strokeWidth={1.75} />
                    </div>
                    <div className="leading-none">
                        <div className="font-outfit text-lg font-semibold tracking-tight text-stone-900">
                            Infini Health<span className="text-emerald-700">Care</span>
                        </div>
                        <div className="text-[9px] font-bold uppercase tracking-[0.22em] text-stone-400 mt-0.5">
                            by CareCo 24
                        </div>
                    </div>
                </Link>

                <nav className="hidden md:flex items-center gap-6 text-sm text-stone-600 font-dmsans">
                    {user && (
                        <>
                            <Link to="/dashboard" data-testid={NAV.dashboardLink} className="hover:text-stone-900 transition-colors">Dashboard</Link>
                            <Link to="/shifts" data-testid={NAV.shiftsLink} className="hover:text-stone-900 transition-colors">Shifts</Link>
                            {user.role === "agency" && (
                                <Link to="/payments" data-testid={NAV.paymentsLink} className="hover:text-stone-900 transition-colors">Payments</Link>
                            )}
                            <Link to="/vault" data-testid={NAV.vaultLink} className="hover:text-stone-900 transition-colors">Vault</Link>
                            <Link to="/notify" data-testid={NAV.notifyLink} className="hover:text-stone-900 transition-colors">Notifications</Link>
                        </>
                    )}
                </nav>

                <div className="flex items-center gap-2">
                    {user ? (
                        <>
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <button
                                        data-testid="nav-account-menu"
                                        className="hidden sm:flex flex-col items-end hover:bg-stone-100 px-3 py-1.5 rounded-full transition-colors"
                                    >
                                        <span className="text-sm font-medium text-stone-900 font-dmsans">{user.full_name}</span>
                                        <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600">{user.role}</span>
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56 rounded-2xl">
                                    <DropdownMenuLabel className="font-dmsans">{user.email}</DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                        data-testid="nav-change-password-link"
                                        onClick={() => nav("/change-password")}
                                        className="cursor-pointer"
                                    >
                                        <KeyRound className="w-4 h-4 mr-2" /> Change password
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                        data-testid={NAV.logoutBtn}
                                        onClick={() => { logout(); nav("/"); }}
                                        className="cursor-pointer text-red-600 focus:text-red-700"
                                    >
                                        <LogOut className="w-4 h-4 mr-2" /> Sign out
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </>
                    ) : (
                        <>
                            <Button
                                data-testid={NAV.signInBtn}
                                onClick={() => nav("/auth?mode=login")}
                                variant="ghost"
                                className="rounded-full text-stone-700"
                            >
                                Sign in
                            </Button>
                            <Button
                                data-testid={NAV.getStartedBtn}
                                onClick={() => nav("/auth?mode=register")}
                                className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-6"
                            >
                                Get started
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </header>
    );
}
